import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { useForm, SubmitHandler, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import JobRequirementsForm from './JobRequirementsForm'; // Adjust path if different
import { useFieldArray } from 'react-hook-form';
import { useApi } from '../services/api'; // FIXED: Import useApi hook for authenticated API
import toast from 'react-hot-toast'; // Added for notifications

// Define enum values as const arrays
const questionCategoryValues = ['TECHNICAL', 'SITUATIONAL', 'CULTURAL'] as const;

const jobSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().min(1, 'Description is required'),
  requirements: z.object({
    requiredQualifications: z.array(z.object({ value: z.string().min(1, 'Qualification cannot be blank') })).min(1, 'At least one qualification required'),
    minExperienceYears: z.number().min(0),
    maxSalary: z.number().optional(),
    workLocation: z.union([z.literal('OFFICE'), z.literal('REMOTE'), z.literal('HYBRID')]),
    requiredLocation: z.string().optional(), // Consolidated to this
    isEmploymentEquity: z.boolean(), // Renamed for clarity
    equityRace: z.string().optional(),
    equityGender: z.string().optional(), // Added
    citizensOnly: z.boolean(),
    requiresWorkAuth: z.boolean(), // Boolean only
    requireCv: z.boolean(),
    requirePayslip: z.boolean(),
    requireTranscript: z.boolean(),
  }).refine(
    (data) => {
      // If not citizens only, work auth MUST be required (legal compliance)
      if (!data.citizensOnly && !data.requiresWorkAuth) {
        return false;
      }
      return true;
    },
    {
      message: 'Work authorization must be required when position is open to non-citizens (legal requirement)',
      path: ['requiresWorkAuth']
    }
  ),
  splits: z.object({
    technical: z.number().min(0).max(100),
    situational: z.number().min(0).max(100),
    culture: z.number().min(0).max(100),
  }).refine((data) => data.technical + data.situational + data.culture === 100, {
    message: 'Question splits must sum to 100%',
  }),
  timeLimit: z.number().min(3, 'Minimum 3 minutes').max(5, 'Maximum 5 minutes'),
  questions: z.array(z.object({
    text: z.string().min(1),
    category: z.union([z.literal('TECHNICAL'), z.literal('SITUATIONAL'), z.literal('CULTURAL')]),
    order: z.number().min(1, 'Order must be at least 1'),
    timeLimit: z.number().min(3, 'Minimum 3 minutes').max(5, 'Maximum 5 minutes'),
  })).min(1, 'At least one question is required. Add one manually or generate questions.'),
});

export type JobFormData = z.infer<typeof jobSchema>;

const JobEdit = () => {
  const api = useApi(); // FIXED: Use the hook to get authenticated api instance
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [maxSalaryDisplay, setMaxSalaryDisplay] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingMessage, setGeneratingMessage] = useState('Generating...');
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updatingMessage, setUpdatingMessage] = useState<string | null>(null);

  const { register, control, handleSubmit, getValues, setValue, formState: { errors }, reset } = useForm<JobFormData>({
    resolver: zodResolver(jobSchema),
    defaultValues: {
      title: '',
      description: '',
      requirements: {
        requiredQualifications: [],
        minExperienceYears: 0,
        workLocation: 'OFFICE',
        isEmploymentEquity: false,
        citizensOnly: false,
        requiresWorkAuth: false,
        requireCv: true,
        requirePayslip: false,
        requireTranscript: false,
      },
      splits: {
        technical: 0,
        situational: 0,
        culture: 0,
      },
      timeLimit: 4,
      questions: [],
    },
  });

  const { fields: questionFields, replace: replaceQuestions, remove, append } = useFieldArray({
    control,
    name: 'questions',
  });

  // Debug hook for questions (remove after verification)
  const watchedQuestions = useWatch({ control, name: 'questions' });
  useEffect(() => {
    console.log('Watched questions (live form state):', watchedQuestions);
  }, [watchedQuestions]);

  useEffect(() => {
    const fetchJob = async () => {
      try {
        const response = await api.get(`/jobs/${id}`);
        const job = response.data;
        const questionsSimplified = job.questions.map((q: any) => ({
          text: q.text,
          category: q.category.toUpperCase(),
          order: q.order,
          timeLimit: q.timeLimit ? Math.min(Math.max(Math.round(q.timeLimit / 60), 3), 5) : 4,
        }));
        const loadedTimeLimit = job.questions.length > 0 ? Math.round(job.questions[0].timeLimit / 60) : 4; // Use first's time, clamp 3-5
        const parsedMaxSalary = job.requirements?.maxSalary ? parseFloat(job.requirements.maxSalary) : undefined; // FIXED: Parse to number
        reset({
          title: job.title,
          description: job.description,
          splits: job.questionSplit || { technical: 33, situational: 33, culture: 34 },
          requirements: {
            requiredQualifications: job.requirements?.requiredQualifications.map((qual: string) => ({ value: qual })) || [],
            minExperienceYears: job.requirements?.minExperienceYears || 0,
            maxSalary: parsedMaxSalary, // FIXED: Use parsed number
            workLocation: job.requirements?.workLocation || 'OFFICE',
            isEmploymentEquity: !!job.requirements?.isEmploymentEquity,
            equityRace: job.requirements?.equityRace,
            equityGender: job.requirements?.equityGender,
            citizensOnly: !!job.requirements?.citizensOnly,
            requiresWorkAuth: !!job.requirements?.requiresWorkAuth,
            requireCv: !!job.requirements?.requireCv,
            requirePayslip: !!job.requirements?.requirePayslip,
            requireTranscript: !!job.requirements?.requireTranscript,
            requiredLocation: job.requirements?.requiredLocation,
          },
          timeLimit: Math.min(Math.max(loadedTimeLimit, 3), 5), // Clamp
          questions: questionsSimplified,
        });
        replaceQuestions(questionsSimplified);
        setMaxSalaryDisplay(parsedMaxSalary ? parsedMaxSalary.toLocaleString('en-US', { minimumFractionDigits: 2 }) : ''); // FIXED: Use parsed for display
      } catch (error: any) {
        console.error('Error fetching job:', error);
        setError('Failed to load job. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchJob();
  }, [id, reset, replaceQuestions, api]); // FIXED: Add api to deps

  const handleMaxSalaryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/[^0-9.,]/g, ''); // Allow numbers, commas, decimal
    setMaxSalaryDisplay(value);
    const parsed = parseFloat(value.replace(/,/g, '')); // Remove commas for number
    setValue('requirements.maxSalary', isNaN(parsed) ? undefined : parsed);
  };

  const handleMaxSalaryBlur = () => {
    if (maxSalaryDisplay) {
      const parsed = parseFloat(maxSalaryDisplay.replace(/,/g, ''));
      if (!isNaN(parsed)) {
        setMaxSalaryDisplay(parsed.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
      }
    }
  };

  const mapCategory = (cat: string) => {
    const upper = cat.toUpperCase();
    if (upper === 'CULTURE') return 'CULTURAL'; // Fix for enum
    return upper;
  };

  const getGenerationErrorMessage = (error: any) => {
    const backendMessage = error.response?.data?.error || error.response?.data?.message;
    if (error.response?.status === 400) {
      return backendMessage || 'Add a job description and make sure the question split totals 100%. Existing questions were preserved.';
    }
    if (error.response?.status === 401 || error.response?.status === 403) {
      return 'Your session is not authorized for AI Interviews. Log in as Jason to continue.';
    }
    return 'AI question regeneration failed. Existing and manual questions were preserved.';
  };

  const getSaveErrorMessage = (error: any) => {
    const backendMessage = error.response?.data?.error || error.response?.data?.message;
    if (typeof backendMessage === 'string' && backendMessage.toLowerCase().includes('company id')) {
      return 'Your admin account is missing company metadata, so AI interview jobs cannot be saved yet.';
    }
    if (error.response?.status === 409) {
      return backendMessage || 'This job already has applications, so it cannot be edited.';
    }
    if (error.response?.status === 400) {
      return backendMessage || 'Invalid data. Check the job fields and try again.';
    }
    if (error.response?.status === 401 || error.response?.status === 403) {
      return 'Your session is not authorized for AI Interviews. Log in as Jason to continue.';
    }
    return backendMessage || 'Failed to update job. Please try again.';
  };

  const handleAddManualQuestion = () => {
    const currentQuestions = getValues('questions') || [];
    append({
      text: '',
      category: 'TECHNICAL',
      order: currentQuestions.length + 1,
      timeLimit: 4,
    });
  };

  const handleGenerateQuestions = async () => {
    setIsGenerating(true);
    setGeneratingMessage('Generating...');
    setGenerationError(null);
    const timeoutId = setTimeout(() => setGeneratingMessage('Almost there...'), 5000);
    const values = getValues();
    try {
      const response = await api.post('/jobs/generate-questions', {
        description: values.description,
        splits: values.splits,
      });
      const generatedQuestions = response.data.questions.map((q: any) => ({
        text: q.text,
        category: mapCategory(q.category), // Normalize to match enum
        order: q.order || 1,
        timeLimit: q.timeLimit ? Math.min(Math.max(Math.round(q.timeLimit / 60), 3), 5) : 4,
      }));
      const orderedGeneratedQuestions = generatedQuestions.map((q: any, index: number) => ({
        ...q,
        order: q.order || index + 1,
      }));
      replaceQuestions(orderedGeneratedQuestions);
      setValue('questions', orderedGeneratedQuestions, { shouldValidate: true, shouldDirty: true }); // Force sync
      setValue('timeLimit', 4); // Reset global time post-regenerate
    } catch (error: any) {
      console.error('Generate questions error:', error);
      setGenerationError(getGenerationErrorMessage(error));
    } finally {
      clearTimeout(timeoutId);
      setIsGenerating(false);
    }
  };

  const onSubmit: SubmitHandler<JobFormData> = async (data) => {
    const mappedData = {
      ...data,
      requirements: {
        ...data.requirements,
        requiredQualifications: data.requirements.requiredQualifications.map((q) => q.value),
      },
      questions: data.questions.map((q, index) => ({
        text: q.text,
        category: mapCategory(q.category), // Ensure enum match
        order: q.order || index + 1,
        timeLimit: q.timeLimit * 60,
      })),
      questionSplit: data.splits,
    };
    setIsUpdating(true);
    setSaveError(null);
    const timeout1 = setTimeout(() => setUpdatingMessage('Nearly there...'), 5000);
    const timeout2 = setTimeout(() => setUpdatingMessage('Almost done...'), 10000);
    try {
      await api.put(`/jobs/${id}`, mappedData);
      toast.success('Job edited successfully'); // FIXED: Add success toast
      navigate('/employer/jobs');
    } catch (error: any) {
      console.error('Error updating job:', error);
      const message = getSaveErrorMessage(error);
      setSaveError(message);
      toast.error(message);
    } finally {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      setUpdatingMessage(null);
      setIsUpdating(false);
    }
  };

  if (loading) return <div className="text-txt-primary">Loading...</div>;
  if (error) return <div className="text-red-500">{error}</div>;

  return (
    <div className="min-h-screen bg-surface-page flex flex-col items-center justify-center px-4 py-8">
      <div className="w-full max-w-4xl bg-gradient-to-br from-gray-800/80 to-gray-700/80 backdrop-blur-md rounded-card p-8 shadow-lg border border-line/50">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <button type="button" onClick={() => navigate('/employer/ai-interviews')} className="text-sm font-semibold text-brand-600 hover:underline">
            Back to AI Interviews
          </button>
          <button type="button" onClick={() => navigate('/employer/jobs')} className="text-sm font-semibold text-brand-600 hover:underline">
            Back to Jobs
          </button>
        </div>
        <h2 className="text-3xl font-semibold text-txt-primary mb-6 text-center">Edit Job</h2>
        <p className="mb-6 text-center text-sm text-txt-secondary">
          Use AI to draft the interview questions from the job description, then edit anything you like before saving.
        </p>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <label className="block text-txt-primary font-medium mb-1">Job Title</label>
            <input 
              {...register('title')} 
              placeholder="Job Title" 
              className="w-full p-3 bg-white text-gray-900 border border-gray-300 rounded-input focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition duration-200 placeholder:text-gray-500" 
            />
            {errors.title?.message && <p className="text-red-500 text-sm mt-1">{errors.title.message as string}</p>}
          </div>
          <div>
            <label className="block text-txt-primary font-medium mb-1">Job Description</label>
            <textarea 
              {...register('description')} 
              placeholder="Job Description" 
              className="w-full p-3 bg-white text-gray-900 border border-gray-300 rounded-input focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition duration-200 h-32 placeholder:text-gray-500" 
            />
            {errors.description?.message && <p className="text-red-500 text-sm mt-1">{errors.description.message as string}</p>}
          </div>

          <JobRequirementsForm control={control} register={register} errors={errors.requirements} maxSalaryDisplay={maxSalaryDisplay} onMaxSalaryChange={handleMaxSalaryChange} onMaxSalaryBlur={handleMaxSalaryBlur} />

          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-txt-primary">Question Splits (%)</h3>
            <div>
              <label className="block text-txt-primary font-medium mb-1">Technical Questions %</label>
              <input 
                type="number" 
                {...register('splits.technical', { valueAsNumber: true })} 
                placeholder="Technical Questions %" 
                className="w-full p-3 bg-white text-gray-900 border border-gray-300 rounded-input focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition duration-200 placeholder:text-gray-500" 
              />
            </div>
            <div>
              <label className="block text-txt-primary font-medium mb-1">Situational Questions % (STAR)</label>
              <input 
                type="number" 
                {...register('splits.situational', { valueAsNumber: true })} 
                placeholder="Situational Questions % (STAR)" 
                className="w-full p-3 bg-white text-gray-900 border border-gray-300 rounded-input focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition duration-200 placeholder:text-gray-500" 
              />
            </div>
            <div>
              <label className="block text-txt-primary font-medium mb-1">Value/Culture Questions %</label>
              <input 
                type="number" 
                {...register('splits.culture', { valueAsNumber: true })} 
                placeholder="Value/Culture Questions % " 
                className="w-full p-3 bg-white text-gray-900 border border-gray-300 rounded-input focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition duration-200 placeholder:text-gray-500" 
              />
            </div>
            {errors.splits?.message && <p className="text-red-500 text-sm mt-1">{errors.splits.message as string}</p>}
          </div>

          <div className="flex justify-center space-x-4">
            <button 
              type="button" 
              onClick={handleGenerateQuestions} 
              disabled={isGenerating}
              className="px-6 py-3 bg-brand-600 text-txt-primary rounded-input hover:bg-brand-600 transition duration-200 disabled:bg-blue-300"
            >
              {isGenerating ? generatingMessage : 'Generate questions with AI'}
            </button>
            <button type="button" onClick={handleAddManualQuestion} className="px-6 py-3 border border-brand-600 text-brand-600 rounded-input hover:bg-brand-50 transition duration-200">
              Add my own question
            </button>
          </div>
          {generationError && <p className="rounded-input border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-900">{generationError}</p>}
          {errors.questions?.message && <p className="text-red-500 text-sm text-center">{errors.questions.message as string}</p>}

          <div className="mt-8">
            <h3 className="text-xl font-semibold text-txt-primary mb-4">Questions (Edit as needed)</h3>
            <div className="grid grid-cols-[80px_1fr_150px_120px_auto] gap-2 mb-2 text-txt-primary font-medium">
              <div>Order</div>
              <div>Question Text</div>
              <div>Category</div>
              <div>Time (min)</div>
              <div>Remove</div>
            </div>
            {questionFields.map((field, index) => (
              <div key={field.id} className="grid grid-cols-[80px_1fr_150px_120px_auto] gap-2 mb-3 items-start">
                <input
                  type="number"
                  min={1}
                  {...register(`questions.${index}.order` as const, { valueAsNumber: true })}
                  className="p-3 bg-white text-gray-900 border border-gray-300 rounded-input focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition duration-200"
                />
                <textarea
                  {...register(`questions.${index}.text` as const)}
                  placeholder="Question text"
                  rows={2}
                  className="p-3 bg-white text-gray-900 border border-gray-300 rounded-input focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition duration-200 placeholder:text-gray-500 whitespace-pre-wrap break-words col-span-1"
                />
                <select
                  {...register(`questions.${index}.category` as const)}
                  className="p-3 bg-white text-gray-900 border border-gray-300 rounded-input focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition duration-200 w-full"
                >
                  {questionCategoryValues.map((category) => (
                    <option key={category} value={category}>{category === 'CULTURAL' ? 'Cultural' : category.charAt(0) + category.slice(1).toLowerCase()}</option>
                  ))}
                </select>
                <input
                  type="number"
                  min={3}
                  max={5}
                  step={1}
                  {...register(`questions.${index}.timeLimit` as const, { valueAsNumber: true })}
                  className="p-3 bg-white text-gray-900 border border-gray-300 rounded-input focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition duration-200"
                />
                <button type="button" onClick={() => remove(index)} className="px-3 py-2 bg-red-500 text-white rounded-input hover:bg-red-600">
                  Remove
                </button>
              </div>
            ))}
          </div>
          {questionFields.length > 0 && (
            <div className="flex justify-center space-x-4">
              <button 
                type="submit" 
                disabled={isUpdating}
                className="px-6 py-3 bg-brand-600 text-txt-primary rounded-input hover:bg-brand-700 transition duration-200 disabled:bg-green-300"
              >
                Update Job
              </button>
            </div>
          )}
          {updatingMessage && <p className="text-center text-txt-primary mt-4">{updatingMessage}</p>}
          {saveError && <p className="rounded-input border border-red-300 bg-red-50 p-3 text-sm text-red-900">{saveError}</p>}
          {questionFields.length === 0 && (
            <p className="text-center text-txt-muted text-sm mt-2">Generate questions with AI or add your own question to enable job update.</p>
          )}
        </form>
      </div>
    </div>
  );
};

export default JobEdit;
